import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  split,
  type DocumentNode,
  type TypedDocumentNode,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { print } from "graphql";
import { ActionCableLink } from "@/lib/action_cable_link";
import { createConsumer } from "@rails/actioncable";

function getCsrfToken(): string {
  if (typeof document === "undefined") return "";
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute("content") || "";
}

const httpLink = new HttpLink({
  uri:
    typeof window !== "undefined" ? `${window.location.origin}/graphql` : "/graphql",
  credentials: "same-origin",
  headers: { "X-CSRF-Token": getCsrfToken() },
  fetch: (input, init) => {
    const headers = new Headers(init?.headers);
    headers.set("X-CSRF-Token", getCsrfToken());
    return fetch(input, { ...init, headers });
  },
});

// Cable subscriptions are browser-only — skip in SSR where `window` is undef.
// httpLink (queries + mutations) still works server-side via the static URI.
const cable =
  typeof window !== "undefined"
    ? createConsumer(`${window.location.origin.replace(/^http/, "ws")}/cable`)
    : null;

const link = cable
  ? split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === "OperationDefinition" &&
          definition.operation === "subscription"
        );
      },
      new ActionCableLink({ cable }),
      httpLink,
    )
  : httpLink;

export const apolloClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { fetchPolicy: "cache-and-network" },
    query: { fetchPolicy: "network-only" },
  },
});

type Variables = Record<string, unknown> | undefined;

// Inferring TData/TVars from a TypedDocumentNode (emitted by graphql-codegen)
// gives end-to-end type safety at migrated call sites, while a plain
// DocumentNode from a gql`...` tag still works with an explicit <TData> generic.
export interface RequestOptions {
  // Ask the browser to finish the request even if the page goes away (tab
  // close, refresh, navigation). It carries the CSRF header, which is why this
  // is preferred over navigator.sendBeacon. Such a request skips Apollo: see
  // `keepaliveRequest`.
  keepalive?: boolean;
}

// A request that has to outlive the page (the lesson notes saved as the tab is
// closed, #1033) goes straight to `fetch`, in the SAME turn as the event that
// asked for it. Through Apollo the real `fetch` ran several asynchronous steps
// later, and when the browser tore the page down in between, the request never
// left: in a browser test, half the closed-tab saves never reached the server.
// The result is read the way Apollo's would be (GraphQL errors throw), and is
// not written to the cache, as for any `no-cache` mutation.
function keepaliveRequest<TData>(
  document: DocumentNode,
  variables: Variables,
): Promise<TData> {
  const definition = getMainDefinition(document);
  const response = fetch(
    typeof window !== "undefined" ? `${window.location.origin}/graphql` : "/graphql",
    {
      method: "POST",
      keepalive: true,
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": getCsrfToken() },
      body: JSON.stringify({
        operationName: definition.kind === "OperationDefinition" ? definition.name?.value : undefined,
        query: print(document),
        variables,
      }),
    },
  );
  return response.then(async (res) => {
    const body = await res.json();
    if (!res.ok || (body.errors && body.errors.length > 0)) {
      const messages = (body.errors ?? []).map((e: { message: string }) => e.message);
      throw new Error(messages.join("\n") || `GraphQL request failed (${res.status})`);
    }
    return body.data as TData;
  });
}

export const graphqlClient = {
  async request<TData = unknown, TVars = Variables>(
    document: TypedDocumentNode<TData, TVars> | DocumentNode,
    variables?: TVars,
    options?: RequestOptions,
  ): Promise<TData> {
    // No `await` before this call: `fetch` must be started in the caller's turn.
    if (options?.keepalive) return keepaliveRequest<TData>(document, variables as Variables);

    const definition = getMainDefinition(document);
    const isMutation =
      definition.kind === "OperationDefinition" &&
      definition.operation === "mutation";

    if (isMutation) {
      const result = await apolloClient.mutate<TData>({
        mutation: document,
        variables: variables as Variables,
        fetchPolicy: "no-cache",
      });
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors.map((e) => e.message).join("\n"));
      }
      return result.data as TData;
    }

    const result = await apolloClient.query<TData>({
      query: document,
      variables: variables as Variables,
      fetchPolicy: "network-only",
    });
    if (result.errors && result.errors.length > 0) {
      throw new Error(result.errors.map((e) => e.message).join("\n"));
    }
    return result.data;
  },
};
